import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { Pressable, StyleSheet, Text, View, type GestureResponderEvent } from '@/ui';
import { tokens } from '@/theme/tokens';
import { useAppTheme, type ThemeColors } from '@/theme/ThemeProvider';

type Variant = 'primary' | 'secondary' | 'ghost';

/**
 * Primary/secondary/ghost buttons. Mirrors prototype .btn-primary/.secondary/.ghost.
 *
 * `primary` is a flat fill in the scheme's INTERACTIVE accent — mint on dark, brand
 * green on light — so every call to action in the app is one colour: the same fill
 * the sponsored CTA, the follow button, the send button and the composer FAB already
 * use. It used to be a dark-green gradient, which on this product's green grounds
 * read as a raised slab of the background rather than the thing you press (#103).
 *
 * `secondary` is drawn in the scheme's SECONDARY accent (#103): a white hairline and
 * white label on dark. It stays deliberately subordinate to the filled primary.
 */
export function BrandButton({
  children,
  onPress,
  variant = 'primary',
  disabled = false,
  trailingChevron = false,
  leadingIcon,
}: {
  children: string;
  onPress?: (e: GestureResponderEvent) => unknown;
  variant?: Variant;
  disabled?: boolean;
  trailingChevron?: boolean;
  leadingIcon?: ReactNode;
}) {
  const { colors: C } = useAppTheme();
  const styles = useMemo(() => createStyles(C), [C]);

  if (variant === 'ghost') {
    return (
      <Pressable
        onPress={onPress}
        disabled={disabled}
        hitSlop={8}
        style={({ pressed }) => [styles.ghost, pressed && styles.pressed, disabled && styles.disabled]}
      >
        <Text style={styles.ghostLabel}>{children}</Text>
        <Text style={styles.ghostChevron}>{'\u203a'}</Text>
      </Pressable>
    );
  }

  if (variant === 'secondary') {
    return (
      <Pressable
        onPress={onPress}
        disabled={disabled}
        style={({ pressed }) => [styles.secondary, pressed && styles.pressed, disabled && styles.disabled]}
      >
        <Text style={styles.secondaryLabel}>{children}</Text>
      </Pressable>
    );
  }

  return (
    <Pressable
      onPress={onPress}
      disabled={disabled}
      style={({ pressed }) => [styles.primary, pressed && styles.pressed, disabled && styles.disabled]}
    >
      <View style={styles.rowCenter}>
        {leadingIcon}
        <Text style={styles.primaryLabel}>{children}</Text>
        {trailingChevron ? <Text style={styles.primaryChevron}>{'\u203a'}</Text> : null}
      </View>
    </Pressable>
  );
}

const RADIUS = tokens.radius.btn; // 13
const HEIGHT = 54;

function createStyles(C: ThemeColors) {
  return StyleSheet.create({
    pressed: { opacity: 0.9, transform: [{ translateY: 1 }] },
    disabled: { opacity: 0.45 },

    primary: {
      height: HEIGHT,
      // Room for the label when the button is sized by its content (a lone
      // CTA in an empty state); a full-width button never feels it.
      paddingHorizontal: 24,
      borderRadius: RADIUS,
      backgroundColor: C.primary,
      alignItems: 'center',
      justifyContent: 'center',
    },
    rowCenter: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 7 },
    primaryLabel: { color: C.onAccent, fontSize: 16, fontWeight: '700', letterSpacing: 0.2 },
    primaryChevron: { color: C.onAccent, fontSize: 20, fontWeight: '500', marginTop: -2 },

    secondary: {
      height: HEIGHT,
      paddingHorizontal: 24,
      borderRadius: RADIUS,
      borderWidth: 1,
      borderColor: C.accentLine,
      backgroundColor: C.accentWash,
      alignItems: 'center',
      justifyContent: 'center',
    },
    secondaryLabel: { color: C.accent, fontSize: 16, fontWeight: '600' },

    ghost: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 3,
      paddingVertical: 10,
      marginTop: 2,
    },
    ghostLabel: { color: C.textDim, fontSize: 15, fontWeight: '600' },
    ghostChevron: { color: C.textDim, fontSize: 19, fontWeight: '500', marginTop: -2 },
  });
}
